import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { LevelJourney } from "@/components/LevelJourney";
import { useAuth } from "@/context/AuthContext";

export default function PlayerLevelCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/assessment-level")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data?.assessment_level) {
    return (
      <div className="card-surface p-5 border-l-4 border-[#FF4500] fade-up" data-testid="player-level-card-locked">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-[#FF4500] flex items-center justify-center shrink-0">
            <Award strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mono mb-1" style={{ color: "#FF4500" }}>SKILL LEVEL · NOT SET</div>
            <h3 className="h-display text-xl sm:text-2xl tracking-tight">Complete your self-rating to lock in a starting level.</h3>
            {user?.dupr_rating && <div className="text-xs text-[#A3A3A3] mt-1" data-testid="player-level-card-dupr">DUPR <span className="text-white font-semibold">{user.dupr_rating}</span></div>}
            <Link to="/onboarding/self-rating" data-testid="player-level-cta-rate" className="btn-volt mt-3 px-4 py-2 text-xs inline-flex items-center gap-2">
              Start self-rating
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { assessment_level: al, progress, next_level: nxt } = data;
  const allCompetent = progress?.all_competent;
  // Defensive fallbacks for legacy records that pre-date the
  // `target` / `starting_level` fields (pre-iter20 bug).
  const ALLOWED = ["2.5", "3.0", "3.5", "4.0", "4.5"];
  const startedAt = al.starting_level
    || (al.history && al.history[0]?.level)
    || al.self_rated
    || al.current;
  const coachConfirmed = Boolean(al.coach_confirmed_at);

  return (
    <div className="card-surface p-5 sm:p-6 fade-up" data-testid="player-level-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 bg-[#CCFF00] flex items-center justify-center shrink-0">
          <Award strokeWidth={2.5} className="w-6 h-6 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-end gap-6 flex-wrap">
            <div>
              <div className="label-mono mb-1">WORKING LEVEL</div>
              <span className="h-display text-3xl sm:text-4xl tracking-tighter text-white" data-testid="player-working-level">{data.working_level || startedAt || "—"}</span>
            </div>
            <div>
              <div className="label-mono mb-1">TARGET LEVEL</div>
              <span className="h-display text-4xl sm:text-5xl tracking-tighter text-[#CCFF00]" data-testid="player-current-level">{al.current}</span>
            </div>
            {user?.dupr_rating && (
              <div data-testid="player-level-card-dupr">
                <div className="label-mono mb-1">DUPR</div>
                <span className="h-display text-3xl sm:text-4xl tracking-tighter text-white">{user.dupr_rating}</span>
              </div>
            )}
          </div>
          <div className="text-[11px] text-[#A3A3A3] mt-1" data-testid="player-target-level">
            Your target level is the Rally Lab level you're working towards — a destination, not a rating.
          </div>
          <div className="text-xs text-[#A3A3A3] mt-1" data-testid="player-started-line">
            Self-rated <span className="text-white">{al.self_rated || "—"}</span>
            {" · "}
            {coachConfirmed
              ? <span className="text-[#CCFF00]">coach-confirmed</span>
              : <span className="text-[#FF4500]">awaiting coach confirmation</span>
            }
          </div>
        </div>
        <div className="text-right">
          <div className="label-mono text-[0.6rem]">PROGRESS</div>
          <div className="h-display text-3xl sm:text-4xl tracking-tighter" data-testid="player-level-percent">{progress?.percent ?? 0}%</div>
        </div>
      </div>

      <div className="mt-5">
        <div className="h-2 bg-[#141414] overflow-hidden">
          <div
            className="h-full transition-all"
            style={{ width: `${progress?.percent ?? 0}%`, background: "#CCFF00" }}
            data-testid="player-level-progress-bar"
          />
        </div>
        <div className="flex flex-wrap justify-between mt-2 gap-x-4 gap-y-1 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
          <span><span className="text-[#CCFF00]">{progress?.competent ?? 0}</span> competent</span>
          {progress?.flagged > 0 && <span><span className="text-[#FF9F1C]">{progress.flagged}</span> focus areas</span>}
          {progress?.waived > 0 && <span><span className="text-[#60A5FA]">{progress.waived}</span> adapted</span>}
          <span><span className="text-[#FF4500]">{progress?.developing ?? 0}</span> developing</span>
          <span><span className="text-[#525252]">{progress?.not_assessed ?? 0}</span> not assessed</span>
          <span>{progress?.total ?? 0} total</span>
        </div>
      </div>

      {allCompetent && nxt && (
        <div className="mt-4 p-3 bg-[#CCFF00]/10 border-l-2 border-[#CCFF00] flex items-center gap-2 text-sm" data-testid="player-level-ready-banner">
          <TrendingUp className="w-4 h-4 text-[#CCFF00] shrink-0" />
          <span className="text-[#E5E5E5]">All skills competent — your coach can promote you to <span className="font-semibold text-[#CCFF00]">{nxt}</span>.</span>
        </div>
      )}
      <div className="mt-5">
        <LevelJourney completed={data.completed_levels} focus={data.focus_areas} waived={data.waived_skills} audience="student" />
      </div>
    </div>
  );
}
